import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Spacer,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  DeliverableArtifactGQLType,
  DeliverableRawNamespaceGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useCompleteFinalOutputDeliverableArtifactInstanceMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/CompleteFinalOutputDeliverableArtifactInstance.gql.generated';
import { useCreateDeliverableArtifactInstanceMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/CreateDeliverableArtifactInstance.gql.generated';
import { useCreateDeliverableContainerReceiptMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/CreateDeliverableContainerReceipt.gql.generated';
import { useGenerateReceiptForDeliverableContainerMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/GenerateReceiptForDeliverableContainer.gql.generated';
import { GetDeliverableArtifactsByContainerIdDocumentGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableArtifactsByContainerId.gql.generated';
import { GetDeliverableContainerByIdDocumentGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableContainerById.gql.generated';
import { useGetFinalOutputDeliverableArtifactInstanceLazyQueryGQLType } from '~/core/routes/deliverables/[deliverableId]/queries/GetFinalOutputDeliverableArtifactInstance.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useProduceFinancialCovenantWorkleaseMessageMutation } from '~/packages/covenants/requests/financialCovenants.gql.generated';
import {
  useEvaluatePortfolioCovenantsMutation,
  useGetCurrentCreditAgreementFullArtifactQuery,
} from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useProduceReportingGenerateMessageMutation } from '~/packages/reporting/requests/reporting.gql.generated';

export const getMostRecentActiveInputDependencyIds = (
  fixedInputDependencies: DeliverableArtifactGQLType[],
) => {
  const ids = fixedInputDependencies.map((d) => {
    const submittedInstance = d.instances.find(
      (i) => i.active && i.status === 'success',
    );

    if (submittedInstance) {
      return submittedInstance.id;
    }
    return null;
  });

  if (ids.some((id) => id === null)) {
    return null;
  }

  const result = ids.flatMap((id) => (id === null ? [] : [id]));
  return result;
};

type GenerateOutputsModalBodyProps = {
  containerId: string;
  containerDueDate: Util.FullDateStr;
  creditFacilityId: string;
  organizationId: string;
  userId: string;
  finalArtifacts: DeliverableArtifactGQLType[];
  fixedInputDependencyInstanceIds: string[];
};
const GenerateOutputsModalBody = ({
  containerId,
  containerDueDate,
  finalArtifacts,
  fixedInputDependencyInstanceIds,
  creditFacilityId,
  userId,
  organizationId,
}: GenerateOutputsModalBodyProps) => {
  const navigate = useNavigate();
  const toast = useToast();

  // functions & logic for generating each domain
  const {
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId },
      },
    ],
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  // generate MSR
  const [produceGenerateMessage] = useProduceReportingGenerateMessageMutation({
    fetchPolicy: 'network-only',
  });
  const generateMSR = useCallback(
    async ({
      reportName,
      reportingDate,
      creditAgreementId,
    }: {
      reportName: string;
      reportingDate: Util.FullDateStr;
      creditAgreementId: string;
    }): Promise<{
      rawNamespace: DeliverableRawNamespaceGQLType;
      rawIdentifier: string;
    }> => {
      // creating MSR report followed by creating a new deliverable artifact instance
      const { data } = await produceGenerateMessage({
        variables: {
          worklease: {
            initiatingOrganizationId: organizationId,
            creditFacilityId,
            creditAgreementId,
            reportName,
            reportingDate,
            mode: 'Live',
            reportTemplateFileType: 'xlsx',
            reportConfigType: 'monthlyServicerReport',
            config: null,
          },
          parameters: null,
        },
      });
      finleyAssert(
        data?.result,
        'Expecting a result from "produceGenerateMessage".',
      );

      return {
        rawNamespace: 'reporting_generate_worklease_status',
        rawIdentifier: commonUtils.generatePgIdentifier({
          column: 'id',
          id: data.result.recordId,
        }),
      };
    },
    [produceGenerateMessage, creditFacilityId, organizationId],
  );

  // generate port cov
  const [evaluatePortfolioCovenants] = useEvaluatePortfolioCovenantsMutation({
    fetchPolicy: 'network-only',
  });
  const generatePortfolioCovenant = useCallback(
    async ({
      covenantKey,
      creditAgreementId,
    }: {
      covenantKey: string;
      creditAgreementId: string;
    }): Promise<{
      rawNamespace: DeliverableRawNamespaceGQLType;
      rawIdentifier: string;
    }> => {
      const { data: evaluatePortCovData } = await evaluatePortfolioCovenants({
        variables: {
          evaluatePortfolioCovenantsRequest: {
            covenantKeys: [covenantKey],
            creditAgreementId,
            creditFacilityId,
            triggerDate: containerDueDate,
            mode: 'RESULT',
            // result mode means we expect to get back exactly one portfolio covenant id corresponding to the record for this covenantKey
            // (e.g. evaluatePortfolioCovenants should return `portfolioCovenantIds.length === 1`)
          },
        },
      });
      finleyAssert(
        evaluatePortCovData?.evaluatePortfolioCovenants,
        'Expecting a result from "evaluatePortfolioCovenants".',
      );
      finleyAssert(
        evaluatePortCovData.evaluatePortfolioCovenants.portfolioCovenantIds
          .length === 1,
        '[Deliverables] Exactly one portfolio covenant should be evaluated at a time.',
      );
      const portfolioCovenantId =
        evaluatePortCovData.evaluatePortfolioCovenants.portfolioCovenantIds[0];

      return {
        rawNamespace: 'portfolio_covenants',
        rawIdentifier: commonUtils.generatePgIdentifier({
          column: 'id',
          id: portfolioCovenantId,
        }),
      };
    },
    [evaluatePortfolioCovenants, containerDueDate, creditFacilityId],
  );

  // generate financial covenant
  const [
    produceFinCovMessageApi,
  ] = useProduceFinancialCovenantWorkleaseMessageMutation({});
  const generateFinancialCovenant = useCallback(
    async ({
      covenantKey,
      creditAgreementId,
    }: {
      covenantKey: string;
      creditAgreementId: string;
    }): Promise<{
      rawNamespace: DeliverableRawNamespaceGQLType;
      rawIdentifier: string;
    }> => {
      const { data } = await produceFinCovMessageApi({
        variables: {
          worklease: {
            covenantKeys: [covenantKey],
            creditAgreementId,
            creditFacilityId,
            triggerDate: containerDueDate,
            mode: 'RESULT',
            // result mode means we expect to get back exactly one financial covenant id corresponding to the record for this covenantKey
            // (e.g. produceFinCovMessageApi should return `financialCovenantKeyToIdMap.length === 1`)
          },
        },
      });
      finleyAssert(
        data?.produceFinancialCovenantWorkleaseMessage,
        'Expecting a result from "produceFinancialCovenantWorkleaseMessage".',
      );
      finleyAssert(
        data.produceFinancialCovenantWorkleaseMessage
          .financialCovenantKeyToIdMap.length === 1,
        '[Deliverables] Exactly one financial covenant should be evaluated at a time.',
      );
      const {
        financialCovenantRecordId,
      } = data.produceFinancialCovenantWorkleaseMessage.financialCovenantKeyToIdMap[0];

      return {
        rawNamespace: 'financial_covenants',
        rawIdentifier: commonUtils.generatePgIdentifier({
          column: 'id',
          id: financialCovenantRecordId,
        }),
      };
    },
    [produceFinCovMessageApi, containerDueDate, creditFacilityId],
  );

  // we push to this list every time an final artifact "completes"
  const [
    successfullyGeneratedFinalOutputInstanceIds,
    setSuccessfullyGeneratedFinalOutputInstanceIds,
  ] = useState<Map<string, string>>(new Map()); // [key = artifact id], [value = artifact instance id]

  // keeps track of the current output that is in-progress / being generated
  const [currentlyPollingInstance, setCurrentlyPollingInstance] = useState<{
    artifactId: string;
    instanceId: string;
  }>();

  // keeps track of loading state if we are waiting on "completeFinalOutputDeliverableArtifactInstanceMutation"
  const [isCompleting, setIsCompleting] = useState<{
    artifactId: string;
  }>();

  // keeps track of the instance that errored
  const [instanceErrored, setInstanceErrored] = useState<{
    artifactId: string;
    instanceId: string;
  }>();

  const [
    finalOutputDeliverableArtifactInstance,
    { data: pollingInstanceData },
  ] = useGetFinalOutputDeliverableArtifactInstanceLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    pollInterval: currentlyPollingInstance ? 1000 : 0, // poll every second if 'currentlyPollingInstance' exists
  });

  const [
    completeFinalOutputDeliverableArtifactInstanceMutation,
    { data: completeFinalArtifactInstanceData },
  ] = useCompleteFinalOutputDeliverableArtifactInstanceMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (completeFinalArtifactInstanceData) {
      const updatedInstance =
        completeFinalArtifactInstanceData
          .completeFinalOutputDeliverableArtifactInstance.instance;
      const errors =
        completeFinalArtifactInstanceData
          .completeFinalOutputDeliverableArtifactInstance.errors;

      if (updatedInstance.status === 'success') {
        setSuccessfullyGeneratedFinalOutputInstanceIds((curr) => {
          const copy = new Map(curr);
          copy.set(updatedInstance.deliverableArtifactId, updatedInstance.id);
          return copy;
        });
      }
      if (updatedInstance.status === 'error' || errors) {
        setInstanceErrored({
          artifactId: updatedInstance.deliverableArtifactId,
          instanceId: updatedInstance.id,
        });
      }
      setIsCompleting(undefined);
    }
  }, [completeFinalArtifactInstanceData]);

  useEffect(() => {
    if (pollingInstanceData) {
      const finalOutputInstance =
        pollingInstanceData.finalOutputDeliverableArtifactInstance;

      if (currentlyPollingInstance) {
        // if the deliverable artifact instance is inProgress but we have a success/error record from the raw pointer,
        // then we want to update the deliverable artifact instance
        const isInProgress = finalOutputInstance.status === 'inProgress';

        const rawPointerIsComplete =
          finalOutputInstance.rawRecordStatus === 'error' ||
          finalOutputInstance.rawRecordStatus === 'success';

        if (
          isInProgress &&
          rawPointerIsComplete &&
          finalOutputInstance.id === currentlyPollingInstance.instanceId &&
          finalOutputInstance.deliverableArtifactId ===
            currentlyPollingInstance.artifactId
        ) {
          // mutation to complete the deliverable artifact instance
          completeFinalOutputDeliverableArtifactInstanceMutation({
            variables: {
              artifactInstanceId: finalOutputInstance.id,
            },
          });
          // mark that we are waiting for the above mutation
          setIsCompleting({
            artifactId: finalOutputInstance.deliverableArtifactId,
          });
          // we no longer need to poll for this artifact instance
          setCurrentlyPollingInstance(undefined);
        }
      }
    }
  }, [
    completeFinalOutputDeliverableArtifactInstanceMutation,
    currentlyPollingInstance,
    pollingInstanceData,
  ]);

  // generate output function
  const generateOutput = useCallback(
    async (artifact: DeliverableArtifactGQLType, creditAgreementId: string) => {
      let result:
        | {
            rawNamespace: DeliverableRawNamespaceGQLType;
            rawIdentifier: string;
          }
        | undefined;
      switch (artifact.config.__typename) {
        case 'MonthlyServicerReportConfig': {
          const reportName = artifact.config.reportName;
          result = await generateMSR({
            reportName,
            reportingDate: artifact.reportingDate,
            creditAgreementId,
          });
          break;
        }
        case 'PortfolioCovenantConfig': {
          const covenantKey = artifact.config.covenantKey;
          result = await generatePortfolioCovenant({
            covenantKey,
            creditAgreementId,
          });
          break;
        }
        case 'FinancialCovenantConfig': {
          const covenantKey = artifact.config.covenantKey;
          result = await generateFinancialCovenant({
            covenantKey,
            creditAgreementId,
          });
          break;
        }
        default:
          throw new Error(
            'Not implemented yet - at this moment we only support generating MSRs and portfolio covenants',
          );
      }
      finleyAssert(
        result,
        'Expected to see a result from generating deliverables outputs.',
      );

      const { rawNamespace, rawIdentifier } = result;
      const {
        data: deliverableArtifactInstanceData,
      } = await createDeliverableArtifactInstanceMutation({
        variables: {
          deliverableArtifactId: artifact.id,
          active: true,
          status: 'inProgress',
          rawDatabase: 'pg',
          rawNamespace,
          rawIdentifier,
        },
      });
      finleyAssert(
        deliverableArtifactInstanceData?.createDeliverableArtifactInstance,
        'Expecting a result from "createDeliverableArtifactInstanceMutation".',
      );

      const instanceId =
        deliverableArtifactInstanceData.createDeliverableArtifactInstance.id;

      // mark as start polling
      setCurrentlyPollingInstance({
        artifactId: artifact.id,
        instanceId,
      });

      // actually begin polling
      finalOutputDeliverableArtifactInstance({
        variables: {
          artifactInstanceId: instanceId,
        },
      });
    },
    [
      finalOutputDeliverableArtifactInstance,
      createDeliverableArtifactInstanceMutation,
      generateFinancialCovenant,
      generateMSR,
      generatePortfolioCovenant,
    ],
  );

  // keeps track of which final artifact (if any) can now be generated
  useEffect(() => {
    if (!creditAgreementData) {
      return;
    }
    // if we are waiting for an artifact instance to complete, do not kick off generating anything new here
    if (isCompleting !== undefined) {
      return;
    }
    // if an instance errored, do not generate any other final artifacts
    if (instanceErrored !== undefined) {
      return;
    }
    // if something is currently polling (in-progress), you cannot generate any other final artifacts
    if (currentlyPollingInstance !== undefined) {
      return;
    }

    // otherwise, find the first final artifact id that is not "completed" (i.e. in 'successfullyGeneratedFinalOutputInstanceIds')
    const nextOutput = finalArtifacts.find(
      (fa) =>
        ![...successfullyGeneratedFinalOutputInstanceIds.keys()].includes(
          fa.id,
        ),
    );

    const creditAgreement = parseCreditAgreementFullArtifact(
      creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement,
    );

    if (nextOutput && creditAgreement) {
      generateOutput(nextOutput, creditAgreement.id);
    }
  }, [
    generateOutput,
    creditAgreementData,
    currentlyPollingInstance,
    finalArtifacts,
    successfullyGeneratedFinalOutputInstanceIds,
    isCompleting,
    instanceErrored,
  ]);

  const [
    createDeliverableContainerReceiptMutation,
  ] = useCreateDeliverableContainerReceiptMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    onCompleted(data) {
      navigate(
        `/deliverables/${containerId}/final-output-report-versions/${data.deliverableContainerReceipt}`,
      );
    },
  });
  useEffect(() => {
    if (
      successfullyGeneratedFinalOutputInstanceIds.size === finalArtifacts.length
    ) {
      // create receipt mutation.. on success to re-direct to final outputs page for the version that was just generated
      createDeliverableContainerReceiptMutation({
        variables: {
          containerId,
          receipt: {
            dependenciesInstanceIds: fixedInputDependencyInstanceIds,
            finalArtifactsInstanceIds: [
              ...successfullyGeneratedFinalOutputInstanceIds.values(),
            ],
          },
          organizationId,
          creditFacilityId,
          userId,
        },
      });
    }
  }, [
    finalArtifacts.length,
    navigate,
    fixedInputDependencyInstanceIds,
    successfullyGeneratedFinalOutputInstanceIds,
    createDeliverableContainerReceiptMutation,
    containerId,
    organizationId,
    creditFacilityId,
    userId,
  ]);

  if (!creditAgreementData) {
    return <Skeleton h="50%" />;
  }

  const creditAgreement = parseCreditAgreementFullArtifact(
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement,
  );

  if (!creditAgreement) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Contact Finley Support.</AlertTitle>
        <AlertDescription>
          A current Credit Agreement must exist to generate outputs for
          deliverables.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Stack>
      {instanceErrored !== undefined ? (
        <Alert status="error" fontWeight="bold" mb={4}>
          <AlertIcon />
          <AlertDescription>
            Errors detected. An email with the error details has been sent.
            Remediate errors and try again.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert status="warning" fontWeight="bold" mb={4}>
          <AlertIcon />
          <AlertDescription>
            Closing the modal will discard all progress. Wait for all processes
            to complete.
          </AlertDescription>
        </Alert>
      )}
      {finalArtifacts.map((artifact) => {
        return (
          <Box key={artifact.id} bgColor="gray.100" padding="15px">
            <HStack>
              <Stack>
                <Text color="black" fontWeight="semibold" fontSize="sm">
                  {`${commonUtils.toTitleCase(artifact.configKey)}: ${
                    artifact.reportingDate
                  }`}
                  <Text
                    color="gray.700"
                    fontWeight="normal"
                    fontSize="xs"
                    mt="3px"
                  >
                    {`Type: ${commonUtils.toTitleCase(
                      artifact.config.artifactType,
                    )}`}
                  </Text>
                </Text>
              </Stack>
              <Spacer />
              {/* eslint-disable-next-line no-nested-ternary */}
              {instanceErrored && instanceErrored.artifactId === artifact.id ? (
                <HStack
                  bgColor="red.100"
                  color="red.300"
                  spacing="12px"
                  px={4}
                  py={2}
                  borderRadius="8px"
                  fontWeight="semibold"
                  fontSize="sm"
                >
                  <Icon icon="circle-xmark" color="red.300" />
                  <Text>Error</Text>
                </HStack>
              ) : [
                  ...successfullyGeneratedFinalOutputInstanceIds.keys(),
                ].includes(artifact.id) ? (
                <HStack
                  bgColor="green.100"
                  color="green.300"
                  spacing="12px"
                  px={4}
                  py={2}
                  borderRadius="8px"
                  fontWeight="semibold"
                  fontSize="sm"
                >
                  <Icon icon="circle-check" color="green.300" />
                  <Text>Complete</Text>
                </HStack>
              ) : (
                <Button
                  px={4}
                  variant="solid"
                  size="sm"
                  bgColor={colors.aurora[400]}
                  color="white"
                  _focus={{ boxShadow: 'none' }}
                  isLoading={
                    artifact.id === currentlyPollingInstance?.artifactId ||
                    artifact.id === isCompleting?.artifactId
                  }
                  isDisabled
                >
                  Waiting
                </Button>
              )}
            </HStack>
          </Box>
        );
      })}
    </Stack>
  );
};

type GenerateOutputsModalProps = {
  containerId: string;
  containerDueDate: Util.FullDateStr;
  creditFacilityId: string;
  organizationId: string;
  userId: string;
  finalArtifacts: DeliverableArtifactGQLType[];
  fixedInputDependencies: DeliverableArtifactGQLType[];
  isOpen: boolean;
  onClose: () => void;
  isGenerating: boolean;
  onGenerate: () => void;
};
export const GenerateOutputsModal = ({
  containerId,
  containerDueDate,
  finalArtifacts,
  fixedInputDependencies,
  creditFacilityId,
  userId,
  organizationId,
  isOpen,
  onClose,
  isGenerating,
  onGenerate,
}: GenerateOutputsModalProps) => {
  const toast = useToast();
  const flags = useFeatureFlags();

  const supportDeliverablesAsyncGeneration =
    flags.supportDeliverablesAsyncGeneration;

  const fixedInputDependencyInstanceIds: string[] | null = useMemo(() => {
    return getMostRecentActiveInputDependencyIds(fixedInputDependencies);
  }, [fixedInputDependencies]);

  const [
    generateReceiptForDeliverableContainerMutation,
  ] = useGenerateReceiptForDeliverableContainerMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      const title = commonUtils.EnvUtils.isCustomerFacing()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast({ ...errorToastConfig, title });
    },
  });

  // This is used to prevent multiple mutations from happening simultaneously.
  // useRef is used instead of useState so updates don’t trigger re-renders.
  const isGenerateReceiptForDeliverableContainerMutationInProgress = useRef(
    false,
  );

  useEffect(() => {
    if (
      !isGenerating &&
      fixedInputDependencyInstanceIds &&
      supportDeliverablesAsyncGeneration === true &&
      !isGenerateReceiptForDeliverableContainerMutationInProgress.current
    ) {
      isGenerateReceiptForDeliverableContainerMutationInProgress.current = true;
      generateReceiptForDeliverableContainerMutation({
        variables: {
          fixedInputDependencies: fixedInputDependencyInstanceIds,
          containerId,
          userId,
        },
      })
        .then(async () => {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          onClose();
        })
        .finally(() => {
          isGenerateReceiptForDeliverableContainerMutationInProgress.current = false;
        });

      onGenerate();
    }
  }, [
    containerId,
    fixedInputDependencyInstanceIds,
    generateReceiptForDeliverableContainerMutation,
    isGenerating,
    onClose,
    onGenerate,
    supportDeliverablesAsyncGeneration,
    userId,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pt="20px" mb={0} pb={0}>
          Generate Version
        </ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody pb="20px">
          {!fixedInputDependencyInstanceIds ? (
            <Text my={2} color="black" fontSize="md">
              Not all required files have completed yet. Please wait for those
              to succeed before generating final outputs.
            </Text>
          ) : (
            <>
              {/* If we do not support async generation, sync generate here.  */}
              {supportDeliverablesAsyncGeneration === false ? (
                <GenerateOutputsModalBody
                  containerId={containerId}
                  containerDueDate={containerDueDate}
                  creditFacilityId={creditFacilityId}
                  organizationId={organizationId}
                  userId={userId}
                  finalArtifacts={finalArtifacts}
                  fixedInputDependencyInstanceIds={
                    fixedInputDependencyInstanceIds
                  }
                />
              ) : (
                <Alert status="warning" fontSize="small" fontWeight="bold">
                  {/* If we support async generate, show notice here. */}
                  <AlertIcon />
                  Your report is being generated. You&apos;ll receive an email
                  notification when it&apos;s ready.
                </Alert>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
